import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Trash2,
  X,
  Send,
  CheckCircle2,
  XCircle,
  MessageSquareText,
  Tags,
} from 'lucide-react';
import Card, { CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import { supabase } from '../lib/supabase';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../context/AuthContext';
import { NotificationService } from '../utils/notificationService';

type CalendarRow = {
  id: string;
  brand: string;
  name: string;
};

type ContentTypeRow = {
  id: string;
  name: string;
  color: string;
};

type ContentCategoryRow = {
  id: string;
  name: string;
  color: string;
};

type CalendarEventRow = {
  id: string;
  calendar_id: string;
  event_date: string; // YYYY-MM-DD
  title: string | null;
  description: string | null;
  content_type_id: string | null;
  category_id: string | null;
  // Legacy (modelo previo)
  content_type?: string | null;
};

type CalendarEventReviewRow = {
  event_id: string;
  status: 'in_review' | 'approved' | 'rejected' | 'commented';
  admin_comment: string | null;
  content_implemented: boolean;
};

const getISODateKey = (date: Date): string => {
  // YYYY-MM-DD estable para comparar con date en Postgres
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Argentina/Buenos_Aires',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return formatter.format(date);
};

const monthNameES = (date: Date): string =>
  date.toLocaleDateString('es-AR', { month: 'long', year: 'numeric' });

const normalizeHex = (hex: string, fallback: string): string => {
  const v = hex?.trim() ?? '';
  if (/^#[0-9a-fA-F]{6}$/.test(v)) return v;
  if (/^[0-9a-fA-F]{6}$/.test(v)) return `#${v}`;
  return fallback;
};

const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
  const v = normalizeHex(hex, '#6366f1').slice(1);
  const r = parseInt(v.slice(0, 2), 16);
  const g = parseInt(v.slice(2, 4), 16);
  const b = parseInt(v.slice(4, 6), 16);
  return { r, g, b };
};

const blendHexColors = (a: string, b: string): string => {
  const ca = normalizeHex(a, '#6366f1');
  const cb = normalizeHex(b, '#64748b');
  const ra = hexToRgb(ca);
  const rb = hexToRgb(cb);
  const r = Math.round((ra.r + rb.r) / 2);
  const g = Math.round((ra.g + rb.g) / 2);
  const bl = Math.round((ra.b + rb.b) / 2);
  return `#${[r, g, bl]
    .map(x => x.toString(16).padStart(2, '0'))
    .join('')}`;
};

const rgbaFromHex = (hex: string, alpha: number): string => {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const ContenidoCalendarioV2: React.FC = () => {
  const { toast, ToastComponent } = useToast();
  const { userRole } = useAuth();

  const [calendars, setCalendars] = useState<CalendarRow[]>([]);
  const [selectedCalendarId, setSelectedCalendarId] = useState<string>('');
  const [loading, setLoading] = useState(true);

  const [contentTypes, setContentTypes] = useState<ContentTypeRow[]>([]);
  const [contentCategories, setContentCategories] = useState<ContentCategoryRow[]>([]);

  const [events, setEvents] = useState<CalendarEventRow[]>([]);

  const [monthCursor, setMonthCursor] = useState<Date>(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<CalendarEventRow | null>(null);
  const [eventForm, setEventForm] = useState({
    title: '',
    content_type_id: '',
    category_id: '',
    event_date: '',
    description: '',
  });

  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [deleteEventId, setDeleteEventId] = useState<string | null>(null);

  const [isCalendarModalOpen, setIsCalendarModalOpen] = useState(false);
  const [calendarForm, setCalendarForm] = useState({ brand: '', name: '' });

  const [isContentTypeModalOpen, setIsContentTypeModalOpen] = useState(false);
  const [editingContentType, setEditingContentType] = useState<ContentTypeRow | null>(null);
  const [contentTypeForm, setContentTypeForm] = useState({ name: '', color: '#4f46e5' });

  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ContentCategoryRow | null>(null);
  const [categoryForm, setCategoryForm] = useState({ name: '', color: '#64748b' });

  const activeMonthStartISO = useMemo(() => {
    const y = monthCursor.getFullYear();
    const m = String(monthCursor.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}-01`;
  }, [monthCursor]);

  const isCurrentMonth = useMemo(() => {
    const now = new Date();
    return now.getFullYear() === monthCursor.getFullYear() && now.getMonth() === monthCursor.getMonth();
  }, [monthCursor]);

  const nextMonthStartISO = useMemo(() => {
    const d = new Date(monthCursor.getFullYear(), monthCursor.getMonth() + 1, 1);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}-01`;
  }, [monthCursor]);

  const [eventReviewsById, setEventReviewsById] = useState<Record<string, CalendarEventReviewRow>>({});
  const [isReviewLoading, setIsReviewLoading] = useState(false);
  const [adminEventCommentDraft, setAdminEventCommentDraft] = useState<string>('');

  const selectedCalendar = useMemo(
    () => calendars.find(c => c.id === selectedCalendarId) ?? null,
    [calendars, selectedCalendarId]
  );

  const contentTypesById = useMemo(() => {
    const m = new Map<string, ContentTypeRow>();
    for (const t of contentTypes) m.set(t.id, t);
    return m;
  }, [contentTypes]);

  const contentCategoriesById = useMemo(() => {
    const m = new Map<string, ContentCategoryRow>();
    for (const c of contentCategories) m.set(c.id, c);
    return m;
  }, [contentCategories]);

  const defaultTypeId = contentTypes[0]?.id ?? '';
  const defaultCategoryId = contentCategories[0]?.id ?? '';

  const eventMapByDateKey = useMemo(() => {
    const map = new Map<string, CalendarEventRow[]>();
    for (const ev of events) {
      const key = ev.event_date;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(ev);
    }
    for (const [, list] of map) {
      list.sort((a, b) => {
        const at = (a.title ?? a.content_type ?? '').toLowerCase();
        const bt = (b.title ?? b.content_type ?? '').toLowerCase();
        return at.localeCompare(bt);
      });
    }
    return map;
  }, [events]);

  const loadCalendars = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('content_calendars')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;

      const rows = (data || []) as CalendarRow[];
      setCalendars(rows);

      if (rows.length > 0) {
        setSelectedCalendarId(prev => (prev ? prev : rows[0].id));
      } else {
        setSelectedCalendarId('');
        setEvents([]);
      }
    } catch (e: any) {
      console.error('Error loading calendars:', e);
      toast.error('Error', 'No se pudieron cargar los calendarios');
    } finally {
      setLoading(false);
    }
  };

  const loadContentTypes = async () => {
    const { data, error } = await supabase.from('content_types').select('*').order('created_at', { ascending: false });
    if (error) throw error;
    setContentTypes((data || []) as ContentTypeRow[]);
  };

  const loadContentCategories = async () => {
    const { data, error } = await supabase
      .from('content_categories')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;
    setContentCategories((data || []) as ContentCategoryRow[]);
  };

  const loadEvents = async (calendarId: string) => {
    if (!calendarId) return;
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('content_calendar_events')
        .select('id, calendar_id, event_date, title, description, content_type_id, category_id, content_type')
        .eq('calendar_id', calendarId)
        .order('event_date', { ascending: true });

      if (error) throw error;
      setEvents((data || []) as CalendarEventRow[]);
    } catch (e: any) {
      console.error('Error loading events:', e);
      toast.error('Error', 'No se pudieron cargar los eventos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void (async () => {
      await loadCalendars();
      // En paralelo, tipado/categorías y eventos se cargan luego (si hay calendario activo)
      try {
        await Promise.all([loadContentTypes(), loadContentCategories()]);
      } catch (e: any) {
        console.error('Error loading types/categories:', e);
        toast.error('Error', 'No se pudieron cargar tipos/categorias');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedCalendarId) return;
    void loadEvents(selectedCalendarId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCalendarId]);

  const refreshEventReviews = async () => {
    if (!selectedCalendarId) {
      setEventReviewsById({});
      return;
    }

    const displayedEventIds = events
      .filter(ev => ev.event_date >= activeMonthStartISO && ev.event_date < nextMonthStartISO)
      .map(ev => ev.id);

    if (displayedEventIds.length === 0) {
      setEventReviewsById({});
      return;
    }

    const { data, error } = await supabase
      .from('content_calendar_event_reviews')
      .select('event_id, status, admin_comment, content_implemented')
      .in('event_id', displayedEventIds);

    if (error) {
      console.error('Error loading event reviews:', error);
      setEventReviewsById({});
      return;
    }

    const map: Record<string, CalendarEventReviewRow> = {};
    for (const row of data || []) {
      const r = row as CalendarEventReviewRow;
      map[r.event_id] = r;
    }
    setEventReviewsById(map);
  };

  useEffect(() => {
    void refreshEventReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCalendarId, activeMonthStartISO, nextMonthStartISO, events]);

  const handleSubmitCalendarForReview = async () => {
    if (!selectedCalendarId || userRole !== 'Contenido') return;
    if (!isCurrentMonth) return;
    if (!selectedCalendar) return;

    setIsReviewLoading(true);
    try {
      const { error } = await supabase.rpc('content_submit_calendar_month_events_for_review', {
        p_calendar_id: selectedCalendarId,
        p_month_start: activeMonthStartISO,
      });
      if (error) throw error;

      await NotificationService.notifyContenidoCalendarSubmittedForReview({
        calendarId: selectedCalendarId,
        brand: selectedCalendar.brand,
        monthLabel: monthNameES(monthCursor),
      });

      toast.success('Enviado a revisión', `Calendario: ${selectedCalendar.brand} — ${monthNameES(monthCursor)}`);
      await refreshEventReviews();
    } catch (e: any) {
      console.error(e);
      toast.error('Error', e?.message || 'No se pudo enviar el calendario a revisión');
    } finally {
      setIsReviewLoading(false);
    }
  };

  const handleAdminDecideEventReview = async (action: 'approved' | 'rejected' | 'commented') => {
    if (!editingEvent) return;
    if (!['Administrador', 'Super Admin'].includes(userRole || '')) return;

    const eventId = editingEvent.id;
    setIsReviewLoading(true);
    try {
      const { error } = await supabase.rpc('admin_decide_calendar_event_review', {
        p_event_id: eventId,
        p_action: action,
        p_comment: adminEventCommentDraft || null,
      });
      if (error) throw error;

      setAdminEventCommentDraft('');
      toast.success(
        'Revisión actualizada',
        action === 'approved' ? 'Aprobado' : action === 'rejected' ? 'Rechazado' : 'Comentario guardado'
      );
      await refreshEventReviews();
      resetEventModalState();
    } catch (e: any) {
      console.error(e);
      toast.error('Error', e?.message || 'No se pudo actualizar la revisión');
    } finally {
      setIsReviewLoading(false);
    }
  };

  const handleContentMarkImplemented = async () => {
    if (!editingEvent) return;
    if (userRole !== 'Contenido') return;

    const review = eventReviewsById[editingEvent.id];
    if (!review || review.status !== 'commented' || !review.admin_comment) return;
    if (review.content_implemented) return;

    setIsReviewLoading(true);
    try {
      const { error } = await supabase.rpc('content_mark_calendar_event_review_implemented', {
        p_event_id: editingEvent.id,
      });
      if (error) throw error;

      toast.success('Listo', 'Marcaste que implementaste los cambios del comentario.');
      await refreshEventReviews();
    } catch (e: any) {
      console.error(e);
      toast.error('Error', e?.message || 'No se pudo marcar como implementado');
    } finally {
      setIsReviewLoading(false);
    }
  };

  const getEventColors = (ev: CalendarEventRow) => {
    const legacyTypeName = ev.content_type ?? '';

    const type =
      (ev.content_type_id ? contentTypesById.get(ev.content_type_id) : undefined) ??
      contentTypes.find(t => t.name === legacyTypeName) ??
      undefined;

    const category =
      (ev.category_id ? contentCategoriesById.get(ev.category_id) : undefined) ??
      undefined;

    const typeColor = type?.color ?? '#4f46e5';
    const categoryColor = category?.color ?? '#64748b';
    const blended = blendHexColors(typeColor, categoryColor);

    return {
      blended,
      background: rgbaFromHex(blended, 0.18),
      borderLeft: blended,
      typeColor,
      categoryColor,
    };
  };

  const resetEventModalState = () => {
    setIsEventModalOpen(false);
    setEditingEvent(null);
    setDeleteEventId(null);
    setIsDeleteConfirmOpen(false);
    setAdminEventCommentDraft('');
    setEventForm({
      title: '',
      content_type_id: '',
      category_id: '',
      event_date: '',
      description: '',
    });
  };

  const openCreateEventForDate = (dateKey: string) => {
    if (!selectedCalendarId) return;
    if (contentTypes.length === 0 || contentCategories.length === 0) {
      toast.error('Faltan tipos o categorias', 'Primero creá al menos un tipo y una categoria.');
      return;
    }

    setEditingEvent(null);
    setEventForm({
      title: '',
      content_type_id: defaultTypeId,
      category_id: defaultCategoryId,
      event_date: dateKey,
      description: '',
    });
    setIsEventModalOpen(true);
  };

  const openEditEvent = (ev: CalendarEventRow) => {
    if (contentTypes.length === 0 || contentCategories.length === 0) {
      toast.error('Faltan tipos o categorias', 'No se puede editar hasta cargar tipos/categorias.');
      return;
    }

    setEditingEvent(ev);
    // Cargar revisión puntual para que el modal muestre comentarios incluso
    // si el usuario abrió el modal después de que se decidiera desde otra sesión.
    void (async () => {
      try {
        const { data, error } = await supabase
          .from('content_calendar_event_reviews')
          .select('event_id, status, admin_comment, content_implemented')
          .eq('event_id', ev.id)
          .maybeSingle();

        if (error) throw error;
        if (!data) {
          setAdminEventCommentDraft('');
          setEventReviewsById(prev => {
            const next = { ...prev };
            delete next[ev.id];
            return next;
          });
          return;
        }

        const row = data as CalendarEventReviewRow;
        setEventReviewsById(prev => ({ ...prev, [row.event_id]: row }));
        setAdminEventCommentDraft(row.admin_comment ?? '');
      } catch (e: any) {
        console.error('Error fetching event review:', e);
      }
    })();
    setEventForm({
      title: ev.title ?? ev.content_type ?? '',
      content_type_id: ev.content_type_id ?? defaultTypeId,
      category_id: ev.category_id ?? defaultCategoryId,
      event_date: ev.event_date,
      description: ev.description ?? '',
    });
    setIsEventModalOpen(true);
  };

  const handleSaveEvent = async () => {
    if (!selectedCalendarId) {
      toast.error('Seleccioná un calendario', 'Primero elegí o creá una marca');
      return;
    }

    const title = eventForm.title.trim();
    const contentTypeId = eventForm.content_type_id;
    const categoryId = eventForm.category_id;
    const eventDate = eventForm.event_date;
    const description = eventForm.description.trim();

    if (!title) {
      toast.error('Falta el titulo', 'Ingresá un titulo para el contenido');
      return;
    }
    if (!contentTypeId) {
      toast.error('Falta el tipo de contenido', 'Seleccioná un tipo');
      return;
    }
    if (!categoryId) {
      toast.error('Falta la categoria', 'Seleccioná una categoria');
      return;
    }
    if (!eventDate) {
      toast.error('Falta la fecha', 'Elegí una fecha');
      return;
    }

    try {
      const user = await supabase.auth.getUser();
      const createdBy = user.data.user?.id ?? null;

      const type = contentTypesById.get(contentTypeId);

      const payload = {
        calendar_id: selectedCalendarId,
        title,
        description: description ? description : null,
        content_type_id: contentTypeId,
        category_id: categoryId,
        event_date: eventDate,
        created_by: createdBy,
        // Compatibilidad con modelo previo (no debería romper incluso si el constraint ya fue relajado)
        content_type: type?.name ?? title,
      };

      if (editingEvent) {
        const { error } = await supabase
          .from('content_calendar_events')
          .update(payload)
          .eq('id', editingEvent.id);
        if (error) throw error;
        toast.success('Evento actualizado');
      } else {
        const { error } = await supabase.from('content_calendar_events').insert(payload);
        if (error) throw error;
        toast.success('Evento creado');
      }

      setIsEventModalOpen(false);
      setEditingEvent(null);
      setEventForm({
        title: '',
        content_type_id: '',
        category_id: '',
        event_date: '',
        description: '',
      });
      await loadEvents(selectedCalendarId);
    } catch (e: any) {
      console.error('Error saving event:', e);
      toast.error('Error', 'No se pudo guardar el evento');
    }
  };

  const handleAskDeleteEvent = (evId: string) => {
    setDeleteEventId(evId);
    setIsDeleteConfirmOpen(true);
  };

  const handleDeleteEvent = async () => {
    if (!deleteEventId) return;
    try {
      const { error } = await supabase.from('content_calendar_events').delete().eq('id', deleteEventId);
      if (error) throw error;
      toast.success('Evento eliminado');
      setIsDeleteConfirmOpen(false);
      setDeleteEventId(null);
      resetEventModalState();
      await loadEvents(selectedCalendarId);
    } catch (e: any) {
      console.error('Error deleting event:', e);
      toast.error('Error', 'No se pudo eliminar el evento');
    }
  };

  const openCalendarCreateModal = () => {
    setCalendarForm({ brand: '', name: '' });
    setIsCalendarModalOpen(true);
  };

  const handleCreateCalendar = async () => {
    if (!calendarForm.brand.trim()) {
      toast.error('Falta la marca', 'Ingresá la marca');
      return;
    }
    try {
      const user = await supabase.auth.getUser();
      const createdBy = user.data.user?.id ?? null;
      const name = calendarForm.name.trim() || calendarForm.brand.trim();

      const payload = {
        brand: calendarForm.brand.trim(),
        name,
        created_by: createdBy,
      };

      const { error } = await supabase.from('content_calendars').insert(payload);
      if (error) throw error;

      toast.success('Calendario creado');
      setIsCalendarModalOpen(false);
      await loadCalendars();
    } catch (e: any) {
      console.error('Error creating calendar:', e);
      toast.error('Error', 'No se pudo crear el calendario');
    }
  };

  const getMonthGridCells = () => {
    const year = monthCursor.getFullYear();
    const month = monthCursor.getMonth();

    const firstDay = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const startWeekday = firstDay.getDay(); // 0 Dom ... 6 Sáb

    // Mostramos un número de semanas completo (múltiplo de 7)
    const totalCells = Math.ceil((startWeekday + daysInMonth) / 7) * 7;
    const cells: Array<{ key: string; dateObj: Date | null; dayNumber: number | null }> = [];

    for (let i = 0; i < totalCells; i++) {
      const dayOffset = i - startWeekday + 1;
      if (dayOffset < 1 || dayOffset > daysInMonth) {
        cells.push({ key: `empty-${i}`, dateObj: null, dayNumber: null });
        continue;
      }
      const dateObj = new Date(Date.UTC(year, month, dayOffset, 12, 0, 0));
      cells.push({
        key: getISODateKey(dateObj),
        dateObj,
        dayNumber: dayOffset,
      });
    }
    return cells;
  };

  const monthCells = useMemo(() => getMonthGridCells(), [monthCursor]);
  const weekDays = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

  const headerSubtitle = selectedCalendar
    ? `Marca: ${selectedCalendar.brand}${selectedCalendar.name && selectedCalendar.name !== selectedCalendar.brand ? ` - ${selectedCalendar.name}` : ''}`
    : 'Creá o seleccioná una marca para ver su calendario';

  const reloadTypesAndCategories = async () => {
    try {
      await Promise.all([loadContentTypes(), loadContentCategories()]);
      // La grilla depende de tipos/categorías para colorear
      if (selectedCalendarId) await loadEvents(selectedCalendarId);
    } catch (e: any) {
      console.error('Reload types/categories failed:', e);
    }
  };

  const openCreateTypeModal = () => {
    setEditingContentType(null);
    setContentTypeForm({ name: '', color: '#4f46e5' });
    setIsContentTypeModalOpen(true);
  };

  const openEditTypeModal = (t: ContentTypeRow) => {
    setEditingContentType(t);
    setContentTypeForm({ name: t.name, color: t.color });
    setIsContentTypeModalOpen(true);
  };

  const handleSaveType = async () => {
    const name = contentTypeForm.name.trim();
    const color = normalizeHex(contentTypeForm.color, '#4f46e5');
    if (!name) {
      toast.error('Falta el nombre', 'Ingresá el nombre del tipo');
      return;
    }
    try {
      const user = await supabase.auth.getUser();
      const createdBy = user.data.user?.id ?? null;

      const payload = { name, color, created_by: createdBy };
      if (editingContentType) {
        const { error } = await supabase.from('content_types').update(payload).eq('id', editingContentType.id);
        if (error) throw error;
        toast.success('Tipo actualizado');
      } else {
        const { error } = await supabase.from('content_types').insert(payload);
        if (error) throw error;
        toast.success('Tipo creado');
      }

      setIsContentTypeModalOpen(false);
      await reloadTypesAndCategories();
    } catch (e: any) {
      console.error('Error saving type:', e);
      toast.error('Error', 'No se pudo guardar el tipo');
    }
  };

  const handleDeleteType = async (typeId: string) => {
    if (!window.confirm('¿Eliminar este tipo? Los eventos asociados también se eliminarán.')) return;
    try {
      const { error } = await supabase.from('content_types').delete().eq('id', typeId);
      if (error) throw error;
      toast.success('Tipo eliminado');
      await reloadTypesAndCategories();
      setIsContentTypeModalOpen(false);
    } catch (e: any) {
      console.error('Error deleting type:', e);
      toast.error('Error', 'No se pudo eliminar el tipo');
    }
  };

  const openCreateCategoryModal = () => {
    setEditingCategory(null);
    setCategoryForm({ name: '', color: '#64748b' });
    setIsCategoryModalOpen(true);
  };

  const openEditCategoryModal = (c: ContentCategoryRow) => {
    setEditingCategory(c);
    setCategoryForm({ name: c.name, color: c.color });
    setIsCategoryModalOpen(true);
  };

  const handleSaveCategory = async () => {
    const name = categoryForm.name.trim();
    const color = normalizeHex(categoryForm.color, '#64748b');
    if (!name) {
      toast.error('Falta el nombre', 'Ingresá el nombre de la categoria');
      return;
    }

    try {
      const user = await supabase.auth.getUser();
      const createdBy = user.data.user?.id ?? null;

      const payload = { name, color, created_by: createdBy };
      if (editingCategory) {
        const { error } = await supabase.from('content_categories').update(payload).eq('id', editingCategory.id);
        if (error) throw error;
        toast.success('Categoria actualizada');
      } else {
        const { error } = await supabase.from('content_categories').insert(payload);
        if (error) throw error;
        toast.success('Categoria creada');
      }

      setIsCategoryModalOpen(false);
      await reloadTypesAndCategories();
    } catch (e: any) {
      console.error('Error saving category:', e);
      toast.error('Error', 'No se pudo guardar la categoria');
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    if (!window.confirm('¿Eliminar esta categoria? Los eventos asociados también se eliminarán.')) return;
    try {
      const { error } = await supabase.from('content_categories').delete().eq('id', categoryId);
      if (error) throw error;
      toast.success('Categoria eliminada');
      await reloadTypesAndCategories();
      setIsCategoryModalOpen(false);
    } catch (e: any) {
      console.error('Error deleting category:', e);
      toast.error('Error', 'No se pudo eliminar la categoria');
    }
  };

  return (
    <div className="w-full max-w-none px-4 lg:px-6 py-5 space-y-4">
      {ToastComponent}

      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between xl:gap-6">
        <div className="min-w-0 shrink">
          <h1 className="text-3xl font-semibold text-gray-900 dark:text-white mb-1">Calendario de contenido</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">{headerSubtitle}</p>
        </div>

        <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:flex-wrap sm:items-end sm:justify-end xl:ml-auto xl:max-w-[min(100%,52rem)]">
          {calendars.length > 0 ? (
            <div className="w-full min-w-0 sm:w-[min(100%,20rem)] sm:flex-1 sm:min-w-[16rem]">
              <Select
                label="Marca activa"
                value={selectedCalendarId}
                onChange={value => setSelectedCalendarId(value)}
                options={calendars.map(c => ({
                  value: c.id,
                  label: `${c.brand}${c.name && c.name !== c.brand ? ` - ${c.name}` : ''}`,
                }))}
                wrapperClassName="mb-0"
                className="h-10"
              />
            </div>
          ) : (
            <p className="text-sm text-gray-600 dark:text-gray-400 sm:max-w-xs sm:text-right">
              No hay calendarios todavía. Creá uno para empezar.
            </p>
          )}

          <Button
            variant="outline"
            size="sm"
            className="shrink-0"
            icon={<Plus className="h-4 w-4" />}
            onClick={openCalendarCreateModal}
          >
            Nuevo calendario
          </Button>

          <div className="flex flex-wrap items-center gap-2 justify-end sm:justify-end">
            <Button variant="outline" size="sm" className="shrink-0" icon={<Tags className="h-4 w-4" />} onClick={openCreateTypeModal}>
              Tipos
            </Button>
            <Button variant="outline" size="sm" className="shrink-0" onClick={openCreateCategoryModal}>
              Categorías
            </Button>
          </div>
        </div>
      </div>

      {(contentTypes.length === 0 || contentCategories.length === 0) && (
        <p className="text-xs text-amber-700 dark:text-amber-300/90 text-right xl:-mt-2">
          Para agendar, primero creá al menos un tipo y una categoría.
        </p>
      )}

      <Card className="w-full">
        <CardContent>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-3">
            <div className="flex items-baseline gap-3 flex-wrap">
              <div className="text-sm text-gray-600 dark:text-gray-400">Vista mensual</div>
              <div className="text-lg font-semibold text-gray-900 dark:text-white">{monthNameES(monthCursor)}</div>
              {isCurrentMonth ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-indigo-50 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-200 border border-indigo-100 dark:border-indigo-900/30">
                  Mes actual
                </span>
              ) : null}
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {userRole === 'Contenido' && isCurrentMonth && selectedCalendarId && selectedCalendar ? (
                <Button
                  size="sm"
                  variant="primary"
                  icon={<Send className="h-4 w-4" />}
                  disabled={isReviewLoading}
                  onClick={handleSubmitCalendarForReview}
                >
                  {isReviewLoading ? 'Enviando...' : 'Enviar a revisión'}
                </Button>
              ) : null}

              <Button
                variant="outline"
                size="sm"
                icon={<ChevronLeft className="h-4 w-4" />}
                onClick={() => setMonthCursor(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}
              >
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                icon={<ChevronRight className="h-4 w-4" />}
                onClick={() => setMonthCursor(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}
              >
                Siguiente
              </Button>
            </div>
          </div>

          {loading && events.length === 0 ? (
            <div className="text-center py-12 text-gray-500">Cargando calendario...</div>
          ) : (
            <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2 text-[11px] text-gray-600 dark:text-gray-300">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-100 dark:bg-green-900/20 dark:text-green-300">
                    <CheckCircle2 className="h-3 w-3" />
                    Aprobado
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-100 dark:bg-red-900/20 dark:text-red-300">
                    <XCircle className="h-3 w-3" />
                    Rechazado
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-100 dark:bg-amber-900/20 dark:text-amber-200">
                    <MessageSquareText className="h-3 w-3" />
                    Con comentario
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-50 text-gray-700 border border-gray-200 dark:bg-gray-900/30 dark:text-gray-200">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-gray-400" />
                    En revisión
                  </span>
                </div>
                <div className="grid grid-cols-7 gap-0">
                  {weekDays.map(day => (
                    <div key={day} className="text-center text-xs font-semibold text-gray-600 dark:text-gray-400 py-2">
                      {day}
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-7 gap-0 border border-gray-200 dark:border-gray-700">
                  {monthCells.map(cell => {
                    if (!cell.dateObj) {
                      return <div key={cell.key} className="min-h-24 bg-gray-50 dark:bg-gray-800" />;
                    }

                    const dateKey = cell.key;
                    const dayEvents = eventMapByDateKey.get(dateKey) ?? [];

                    return (
                      <div
                        key={cell.key}
                        className="min-h-[92px] p-2 border border-gray-200 dark:border-gray-700/0 bg-white dark:bg-gray-900"
                        role="button"
                        tabIndex={0}
                        onClick={() => openCreateEventForDate(dateKey)}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') openCreateEventForDate(dateKey);
                        }}
                      >
                        <div className="text-[11px] font-semibold text-gray-700 dark:text-gray-200 mb-1 flex items-center justify-between gap-1">
                          <span>{cell.dayNumber}</span>
                          {dayEvents.length > 0 ? (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-200 border border-indigo-100 dark:border-indigo-900/30">
                              {dayEvents.length > 9 ? '9+' : dayEvents.length}
                            </span>
                          ) : null}
                        </div>

                        <div className="space-y-1">
                          {dayEvents.slice(0, 3).map(ev => {
                            const colors = getEventColors(ev);
                            const type = ev.content_type_id ? contentTypesById.get(ev.content_type_id) : undefined;
                            const category = ev.category_id ? contentCategoriesById.get(ev.category_id) : undefined;
                            const review = eventReviewsById[ev.id];

                            return (
                              <div
                                key={ev.id}
                                className="text-[11px] leading-snug p-1 rounded cursor-pointer hover:opacity-95 transition-opacity border"
                                style={{ backgroundColor: colors.background, borderColor: colors.blended, borderLeftWidth: 4 }}
                                onClick={e => {
                                  e.stopPropagation();
                                  openEditEvent(ev);
                                }}
                              >
                                <div className="font-medium text-gray-900 dark:text-white break-words whitespace-normal">
                                  {ev.title ?? ev.content_type ?? '(Sin titulo)'}
                                </div>
                                <div className="text-[10px] leading-snug text-gray-600 dark:text-gray-200 break-words whitespace-normal mt-0.5">
                                  {(type?.name ?? '').trim()}
                                  {category?.name ? ` / ${category.name}` : ''}
                                </div>
                                {review?.status ? (
                                  <div className="mt-0.5 flex flex-wrap items-center gap-1">
                                    {review.status === 'approved' ? (
                                      <span className="inline-flex items-center text-green-700 dark:text-green-300" title="Aprobado">
                                        <CheckCircle2 className="h-3 w-3" />
                                      </span>
                                    ) : null}
                                    {review.status === 'rejected' ? (
                                      <span className="inline-flex items-center text-red-700 dark:text-red-300" title="Rechazado">
                                        <XCircle className="h-3 w-3" />
                                      </span>
                                    ) : null}
                                    {review.status === 'commented' ? (
                                      <span className="inline-flex items-center gap-1" title={review.admin_comment ? 'Con comentario' : 'Comentado'}>
                                        <MessageSquareText className="h-3 w-3 text-amber-700 dark:text-amber-300" />
                                        {review.content_implemented ? (
                                          <CheckCircle2 className="h-3 w-3 text-green-700 dark:text-green-300" />
                                        ) : null}
                                      </span>
                                    ) : null}
                                    {review.status === 'in_review' ? (
                                      <span className="inline-flex items-center text-gray-500 dark:text-gray-400" title="En revisión">
                                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-gray-400" />
                                      </span>
                                    ) : null}
                                  </div>
                                ) : null}
                              </div>
                            );
                          })}
                          {dayEvents.length > 3 && (
                            <div className="text-[10px] text-gray-500 dark:text-gray-400 pl-1">
                              +{dayEvents.length - 3} más
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

      {isCalendarModalOpen &&
        createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Nuevo calendario</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Creá un calendario para una marca y agendá por fecha.</p>
              </div>
              <button
                className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-200"
                onClick={() => setIsCalendarModalOpen(false)}
                aria-label="Cerrar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <Input
                label="Marca *"
                value={calendarForm.brand}
                onChange={e => setCalendarForm(prev => ({ ...prev, brand: e.target.value }))}
                placeholder="Ej: Productex, Marca X..."
              />
              <Input
                label="Nombre (opcional)"
                value={calendarForm.name}
                onChange={e => setCalendarForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Si lo dejás vacío, se usa la marca"
              />
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setIsCalendarModalOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleCreateCalendar} icon={<Plus className="h-5 w-5" />}>
                Crear
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {isEventModalOpen &&
        createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4 sm:p-6">
          <div
            className="bg-white dark:bg-gray-800 w-[80vw] max-w-[80vw] max-h-[90vh] shadow-xl rounded-xl overflow-hidden flex flex-col border border-gray-200 dark:border-gray-700"
            role="dialog"
            aria-modal="true"
            aria-labelledby="contenido-event-modal-title"
          >
            <div className="overflow-y-auto flex-1 min-h-0 p-6 sm:p-8">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h3 id="contenido-event-modal-title" className="text-lg font-semibold text-gray-900 dark:text-white">
                  {editingEvent ? 'Editar contenido' : 'Programar contenido'}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Titulo, tipo, categoria, fecha y descripción.</p>
              </div>
              <button
                className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-200"
                onClick={resetEventModalState}
                aria-label="Cerrar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <Input
                label="Titulo *"
                value={eventForm.title}
                onChange={e => setEventForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Ej: Post Instagram - Campaña de verano"
              />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Select
                  label="Tipo de contenido *"
                  value={eventForm.content_type_id}
                  onChange={value => setEventForm(prev => ({ ...prev, content_type_id: value }))}
                  options={contentTypes.map(t => ({ value: t.id, label: t.name }))}
                  wrapperClassName="mb-0"
                  className="h-10"
                />

                <Select
                  label="Categoria *"
                  value={eventForm.category_id}
                  onChange={value => setEventForm(prev => ({ ...prev, category_id: value }))}
                  options={contentCategories.map(c => ({ value: c.id, label: c.name }))}
                  wrapperClassName="mb-0"
                  className="h-10"
                />

                <Input
                  label="Fecha *"
                  type="date"
                  value={eventForm.event_date}
                  onChange={e => setEventForm(prev => ({ ...prev, event_date: e.target.value }))}
                  className="!mb-0"
                />
              </div>

              <Input
                label="Descripcion"
                multiline
                rows={4}
                value={eventForm.description}
                onChange={e => setEventForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Detalles adicionales..."
              />

              <div className="flex gap-2 items-center">
                <div
                  className="w-6 h-6 rounded border"
                  style={{
                    backgroundColor: rgbaFromHex(
                      blendHexColors(
                        contentTypesById.get(eventForm.content_type_id)?.color ?? '#4f46e5',
                        contentCategoriesById.get(eventForm.category_id)?.color ?? '#64748b'
                      ),
                      0.35
                    ),
                    borderColor: blendHexColors(
                      contentTypesById.get(eventForm.content_type_id)?.color ?? '#4f46e5',
                      contentCategoriesById.get(eventForm.category_id)?.color ?? '#64748b'
                    ),
                  }}
                />
                <div className="text-xs text-gray-600 dark:text-gray-300">
                  El color se define por tipo + categoria
                </div>
              </div>
            </div>

            {editingEvent ? (
              (() => {
                const review = eventReviewsById[editingEvent.id];
                if (!review) return null;

                const isAdmin = ['Administrador', 'Super Admin'].includes(userRole || '');
                const isContenido = userRole === 'Contenido';

                return (
                  <div className="mt-4 border-t pt-4 space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="text-sm font-semibold text-gray-900 dark:text-white">
                          Estado de revisión
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                          {review.status === 'approved'
                            ? 'Aprobado'
                            : review.status === 'rejected'
                              ? 'Rechazado'
                              : review.status === 'commented'
                                ? 'Con comentario'
                                : 'En revisión'}
                        </div>
                      </div>
                      {review.status === 'approved' ? (
                        <span className="inline-flex items-center gap-2 text-green-700 dark:text-green-300 text-xs font-medium">
                          <CheckCircle2 className="h-4 w-4" />
                          Aprobado
                        </span>
                      ) : null}
                    </div>

                    {review.admin_comment ? (
                      <div className="space-y-1">
                        <div className="text-xs font-medium text-gray-700 dark:text-gray-200">Comentario</div>
                        <div className="text-sm text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md p-3">
                          <div className="flex items-start justify-between gap-3">
                            <div className="whitespace-pre-wrap">{review.admin_comment}</div>
                            {isContenido && review.status === 'commented' ? (
                              <label className="inline-flex items-center gap-2 text-xs text-gray-700 dark:text-gray-200">
                                <input
                                  type="checkbox"
                                  checked={review.content_implemented}
                                  onChange={() => {
                                    void handleContentMarkImplemented();
                                  }}
                                  disabled={isReviewLoading || review.content_implemented}
                                />
                                Implementado
                              </label>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    ) : null}

                    {isAdmin ? (
                      <div className="space-y-2">
                        <div className="text-xs font-medium text-gray-700 dark:text-gray-200">
                          {review.status === 'commented' ? 'Editar comentario' : 'Agregar comentario (si corresponde)'}
                        </div>
                        <textarea
                          value={adminEventCommentDraft}
                          onChange={e => setAdminEventCommentDraft(e.target.value)}
                          className="w-full min-h-[90px] rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder="Escribí el comentario o motivo de la decisión..."
                        />
                        <div className="flex items-center gap-2 flex-wrap">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={isReviewLoading}
                            onClick={() => void handleAdminDecideEventReview('commented')}
                          >
                            Comentar
                          </Button>
                          <Button
                            size="sm"
                            variant="primary"
                            disabled={isReviewLoading}
                            onClick={() => void handleAdminDecideEventReview('approved')}
                          >
                            Aprobar
                          </Button>
                          <Button
                            size="sm"
                            variant="danger"
                            disabled={isReviewLoading}
                            onClick={() => void handleAdminDecideEventReview('rejected')}
                          >
                            Rechazar
                          </Button>
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })()
            ) : null}

            <div className="flex justify-end gap-2 mt-6">
              {editingEvent ? (
                <Button
                  variant="outline"
                  icon={<Trash2 className="h-4 w-4" />}
                  onClick={() => handleAskDeleteEvent(editingEvent.id)}
                  className="border-red-300 text-red-600 hover:bg-red-50 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-900/20"
                >
                  Eliminar
                </Button>
              ) : null}
              <Button variant="outline" onClick={resetEventModalState}>
                Cancelar
              </Button>
              <Button onClick={handleSaveEvent}>{editingEvent ? 'Actualizar' : 'Crear'}</Button>
            </div>

            <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
              Tipos y categorías se gestionan con los botones Tipos y Categorías arriba a la derecha.
            </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {isDeleteConfirmOpen &&
        createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[110] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Confirmar eliminación</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              Esta acción no se puede deshacer. ¿Querés eliminar este evento del calendario?
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsDeleteConfirmOpen(false)}>
                Cancelar
              </Button>
              <Button variant="danger" onClick={handleDeleteEvent} className="bg-red-600 hover:bg-red-700 text-white">
                Eliminar
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {isContentTypeModalOpen &&
        createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Gestionar tipos de contenido</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Creá, editá o eliminá tipos.</p>
              </div>
              <button
                className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-200"
                onClick={() => setIsContentTypeModalOpen(false)}
                aria-label="Cerrar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <Input
                label="Nombre *"
                value={contentTypeForm.name}
                onChange={e => setContentTypeForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ej: Instagram, Campaña, Email..."
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Color</label>
                <input
                  type="color"
                  value={normalizeHex(contentTypeForm.color, '#4f46e5')}
                  onChange={e => setContentTypeForm(prev => ({ ...prev, color: e.target.value }))}
                  className="w-full h-10 rounded-md cursor-pointer"
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setIsContentTypeModalOpen(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleSaveType}>{editingContentType ? 'Actualizar' : 'Crear'}</Button>
              </div>
            </div>

            <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-4 space-y-3">
              {contentTypes.length === 0 ? (
                <div className="text-sm text-gray-600 dark:text-gray-400">No hay tipos todavía.</div>
              ) : (
                contentTypes.map(t => (
                  <div key={t.id} className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-7 h-7 rounded border" style={{ backgroundColor: t.color, borderColor: t.color }} />
                      <div className="min-w-0">
                        <div className="font-medium text-gray-900 dark:text-white truncate">{t.name}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{t.color}</div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => openEditTypeModal(t)}>
                        Editar
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDeleteType(t.id)}
                        className="bg-red-600 hover:bg-red-700 text-white"
                      >
                        Eliminar
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {isCategoryModalOpen &&
        createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Gestionar categorias</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Creá, editá o eliminá categorias.</p>
              </div>
              <button
                className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-200"
                onClick={() => setIsCategoryModalOpen(false)}
                aria-label="Cerrar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <Input
                label="Nombre *"
                value={categoryForm.name}
                onChange={e => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ej: Redes, Web, Campañas..."
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Color</label>
                <input
                  type="color"
                  value={normalizeHex(categoryForm.color, '#64748b')}
                  onChange={e => setCategoryForm(prev => ({ ...prev, color: e.target.value }))}
                  className="w-full h-10 rounded-md cursor-pointer"
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setIsCategoryModalOpen(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleSaveCategory}>{editingCategory ? 'Actualizar' : 'Crear'}</Button>
              </div>
            </div>

            <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-4 space-y-3">
              {contentCategories.length === 0 ? (
                <div className="text-sm text-gray-600 dark:text-gray-400">No hay categorias todavía.</div>
              ) : (
                contentCategories.map(c => (
                  <div key={c.id} className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-7 h-7 rounded border" style={{ backgroundColor: c.color, borderColor: c.color }} />
                      <div className="min-w-0">
                        <div className="font-medium text-gray-900 dark:text-white truncate">{c.name}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{c.color}</div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => openEditCategoryModal(c)}>
                        Editar
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDeleteCategory(c.id)}
                        className="bg-red-600 hover:bg-red-700 text-white"
                      >
                        Eliminar
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ContenidoCalendarioV2;

